// src/features/hmi-simulator/components/screens/StaffScreen.tsx
import React from 'react';
import { 
  Box,
  Paper,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  Container,
  Stack
} from '@mui/material';
import { 
  Refresh, 
  Lock, 
  Close,
  Logout
} from '@mui/icons-material';
import { HMIConfig } from '../../types';

export interface StaffScreenProps {
  config: HMIConfig;
  staffSession: string | null;
  staffPassword: string;
  setStaffPassword: (value: string) => void;
  staffError: string;
  lockerStatuses: Array<{lockerName: string; lockerAvailable: boolean}>;
  selectedLocker: string;
  setSelectedLocker: (value: string) => void;
  message: string;
  apiLoading: boolean;
  handleStaffLogin: () => void;
  handleRefreshLockers: () => void;
  handleStaffOpenLocker: () => void;
  setStaffSession: (value: string | null) => void;
  setScreen: (screen: any) => void;
}

export const StaffScreen: React.FC<StaffScreenProps> = ({
  config,
  staffSession,
  staffPassword,
  setStaffPassword,
  staffError,
  lockerStatuses,
  selectedLocker,
  setSelectedLocker,
  message,
  apiLoading,
  handleStaffLogin,
  handleRefreshLockers,
  handleStaffOpenLocker,
  setStaffSession,
  setScreen
}) => {
  
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
    
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {!staffSession ? (
          // Pantalla de Login
          <Card sx={{ maxWidth: 600, mx: 'auto' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5">Staff Login</Typography>
                <IconButton onClick={() => setScreen('main')}>
                  <Close />
                </IconButton>
              </Box>
              
              <Typography color="text.secondary" paragraph>
                Enter staff password to access locker control panel
              </Typography>
              
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  type="password"
                  label="Staff Password"
                  value={staffPassword}
                  onChange={(e) => setStaffPassword(e.target.value)}
                  placeholder="Enter password"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && staffPassword) {
                      handleStaffLogin();
                    }
                  }}
                />
                
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleStaffLogin}
                  disabled={!staffPassword || apiLoading}
                  size="large"
                  sx={{ backgroundColor: config.primaryColor }}
                >
                  {apiLoading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
                </Button>
                
                {staffError && (
                  <Alert severity="error">{staffError}</Alert>
                )}
                
                <Typography variant="caption" color="text.secondary" align="center">
                  Test password: 123456
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          // Pantalla de Control de Lockers
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5">Locker Control Panel</Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    startIcon={<Refresh />}
                    onClick={() => handleRefreshLockers()}
                    variant="outlined"
                  >
                    Refresh
                  </Button>
                  <Button
                    startIcon={<Logout />}
                    onClick={() => {
                      setStaffSession(null);
                      setSelectedLocker('');
                    }}
                    variant="outlined"
                    color="error"
                  >
                    Logout
                  </Button>
                </Stack>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              {/* Grid de Lockers */}
              <Typography variant="h6" gutterBottom>
                Locker Status
              </Typography>

              {lockerStatuses.length > 0 ? (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {lockerStatuses.map((locker) => (
                      <Grid item xs={6} sm={4} md={3} lg={2} key={locker.lockerName}>
                        <Paper
                          elevation={selectedLocker === locker.lockerName ? 4 : 1}
                          sx={{
                            p: 2,
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: !locker.lockerAvailable 
                              ? '#ffebee'
                              : selectedLocker === locker.lockerName 
                                ? `${config.primaryColor}15`
                                : '#e8f5e9',
                            border: selectedLocker === locker.lockerName 
                              ? `2px solid ${config.primaryColor}`
                              : '2px solid transparent',
                            transition: 'all 0.3s',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 2
                            }
                          }}
                          onClick={() => {
                            setSelectedLocker(locker.lockerName);
                          }}
                        >
                          <Typography variant="h4" sx={{ 
                            fontWeight: 'bold',
                            color: !locker.lockerAvailable ? '#d32f2f' : '#2e7d32'
                          }}>
                            {locker.lockerName}
                          </Typography>
                          <Chip
                            label={locker.lockerAvailable ? 'Available' : 'Occupied'}
                            color={locker.lockerAvailable ? 'success' : 'error'}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                  
                  {/* Controles */}
                  {selectedLocker && (
                    <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                      <Stack spacing={2}>
                        <Typography variant="subtitle1">
                          Selected Locker: <strong>{selectedLocker}</strong>
                          {' - '}
                          <Chip 
                            label={lockerStatuses.find(l => l.lockerName === selectedLocker)?.lockerAvailable ? 'Available' : 'Occupied'}
                            color={lockerStatuses.find(l => l.lockerName === selectedLocker)?.lockerAvailable ? 'success' : 'error'}
                            size="small"
                          />
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary">
                          {lockerStatuses.find(l => l.lockerName === selectedLocker)?.lockerAvailable 
                            ? 'This locker is currently available. Opening it will allow manual assignment.'
                            : 'This locker is currently occupied. Opening it will allow the guest to retrieve their belongings.'}
                        </Typography>
                        
                        <Stack direction="row" spacing={2}>
                          <Button
                            variant="contained"
                            startIcon={<Lock />}
                            onClick={handleStaffOpenLocker}
                            disabled={apiLoading}
                            sx={{ backgroundColor: config.primaryColor }}
                          >
                            {apiLoading ? 'Opening...' : `Open Locker ${selectedLocker}`}
                          </Button>
                          
                          <Button
                            variant="outlined"
                            onClick={() => setSelectedLocker('')}
                          >
                            Cancel Selection
                          </Button>
                        </Stack>
                        
                        {message && (
                          <Alert severity="success">{message}</Alert>
                        )}
                      </Stack>
                    </Paper>
                  )}
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Loading lockers...</Typography>
                </Box>
              )}
              
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};
